import {Extension} from "@tiptap/react";
import {v4 as uuidv4} from "uuid";
import {Plugin} from 'prosemirror-state';

export const Attributes = Extension.create({
    name: "attributes",
    addGlobalAttributes() {
        return [{
            types: ["paragraph", "heading", "orderedList", "bulletList", "table"],
            attributes: {
                class: {
                    default: null,
                    parseHTML: element => element.getAttribute("class"),
                    renderHTML: attributes => {
                        if (!attributes.class) return {}
                        return {class: attributes.class}
                    },
                },
                id: {
                    default: null,
                    parseHTML: element => {
                        if (element.closest("li")) return null;
                        return element.getAttribute("id");
                    },
                    renderHTML: attributes => {
                        if (!attributes.id) return {}
                        return {id: attributes.id}
                    }
                }
            }
        }]
    },
})

export const NodeIdPlugin = new Plugin({
    appendTransaction: (transactions, oldState, newState) => {
        let tr = newState.tr
        let modified = false
        const usedIds = new Set()

        newState.doc.descendants((node, pos) => {
            const {name} = node.type

            if (!["paragraph", "heading", "orderedList", "bulletList", "table"].includes(name)) return

            const $pos = newState.doc.resolve(pos)
            const inList = $pos.parent.type.name === "listItem"

            if (name === "paragraph" && inList) {
                if (node.attrs.id !== null) {
                    tr = tr.setNodeMarkup(pos, undefined, {...node.attrs, id: null})
                    modified = true
                }
                return
            }

            let id = node.attrs.id
            if (!id || usedIds.has(id)) id = uuidv4()
            usedIds.add(id)

            if (id !== node.attrs.id) {
                tr = tr.setNodeMarkup(pos, undefined, {...node.attrs, id})
                modified = true
            }
        })

        return modified ? tr : null
    },
})

export const LineHeight = Extension.create({
    name: "lineHeight",
    addOptions() {
        return {types: ["paragraph", "heading"]};
    },
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                lineHeight: {
                    default: null,
                    parseHTML: el => el.style.lineHeight || null,
                    renderHTML: attributes => {
                        if (!attributes.lineHeight) return {};
                        return {style: `line-height: ${attributes.lineHeight}`};
                    },
                },
            },
        }];
    },
    addCommands() {
        return {
            setLineHeight: (lineHeight) => ({commands}) => {
                return this.options.types.every(type =>
                    commands.updateAttributes(type, {lineHeight})
                );
            },
        };
    },
});

const MARGIN_STEP = 50;

export const Margin = Extension.create({
    name: "margin",
    addOptions() {
        return {types: ["paragraph", "heading", "orderedList", "bulletList"]};
    },
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                marginLeft: {
                    default: 0,
                    parseHTML: el => parseInt(el.style.marginLeft) || 0,
                    renderHTML: attributes => {
                        if (!attributes.marginLeft) return {};
                        return {style: `margin-left: ${attributes.marginLeft}px`};
                    },
                },
            },
        }]
    },
    addCommands() {
        return {
            indentIn: () => ({commands, state}) => {
                const {from, to} = state.selection;

                state.doc.nodesBetween(from, to, (node, pos, parent) => {
                    if (!this.options.types.includes(node.type.name) || parent?.type.name === 'listItem') return;

                    const current = node.attrs.marginLeft || 0;
                    commands.updateAttributes(node.type.name, {
                        marginLeft: Math.min(node.type.name === "paragraph" ? 630 : 590, current + MARGIN_STEP)
                    });
                });

                return true;
            },
            indentOut: () => ({commands, state}) => {
                const {from, to} = state.selection;

                state.doc.nodesBetween(from, to, (node, pos, parent) => {
                    if (!this.options.types.includes(node.type.name) || parent?.type.name === 'listItem') return;

                    const current = node.attrs.marginLeft || 0;
                    commands.updateAttributes(node.type.name, {
                        marginLeft: Math.max(0, current - MARGIN_STEP)
                    });
                });

                return true;
            },
        };
    },
});
